/* ==========================================================================
   tl-bubble.js — Tokyo Lifestyle · Bubble Blasters landing page
   --------------------------------------------------------------------------
   Two things live in this file:

     1. BubbleField — the canvas engine. A real soap-bubble renderer: thin-film
        iridescent rim, specular highlight, buoyancy + drift physics, wobble,
        tap-to-pop with droplet burst, and scroll-reactive spawning.

     2. The page — bag state, variant picker, running totals, delivery area,
        the pre-filled COD checkout hand-off, i18n (EN/BN) and theme.

   How the engine stays fast without dropping quality
   --------------------------------------------------
   Nearly all of this page's traffic arrives in the Facebook / Instagram in-app
   browser on mid-range Android, and the brief was "rich everywhere". Those two
   are only compatible because of ONE technique: every bubble is drawn from a
   PRE-RENDERED SPRITE. The expensive part of a convincing bubble is its
   gradients — a conic iridescent rim, an offset body wash, a specular ellipse.
   Building those per bubble per frame is what makes canvas toys jank on phones.
   Here they are rendered ONCE into a handful of offscreen canvases at boot, and
   the per-frame cost collapses to a `drawImage` with a transform. ~120 of those
   is nothing, even on an A9.

   So the visual quality is IDENTICAL on every device. Only the bubble COUNT
   adapts, and it adapts to measured frame time rather than to a guess about the
   device — see `retune()`. That is the honest reading of "rich everywhere":
   never a downgraded look, just a density that keeps the page at 60fps.

   ES5-level syntax throughout (var, no arrow functions, no template literals,
   no const/let) because the in-app browsers on older Android are the target and
   a single parse error means a blank page instead of a degraded one.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('tlb-root');
  if (!root) return;

  var DATA = {};
  try {
    var dataEl = document.getElementById('tlb-data');
    if (dataEl) DATA = JSON.parse(dataEl.textContent || '{}');
  } catch (e) {
    DATA = {};
  }

  var STYLES = DATA.styles || [];
  var REFILL = DATA.refill || null;
  var DELIVERY = DATA.delivery || { inside: 70, outside: 130 };
  var FREE_OVER = DATA.freeOver || 0;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ══════════════════════════════════════════════════════════════════════════
     1. BUBBLE FIELD — the canvas engine
     ══════════════════════════════════════════════════════════════════════════ */

  /* Thin-film interference does not produce arbitrary colours — a soap film
     sweeps through a specific sequence as its thickness varies. These five
     stops approximate that sweep, which is why the rim reads as *soap* and not
     as a generic rainbow. Each sprite rotates the sequence start, so a field of
     bubbles shows related-but-different films rather than N identical copies. */
  var FILM_DARK = ['#7ff0e0', '#9db4ff', '#c79bff', '#ff8fd0', '#ffe58a'];

  /* The same sweep, pushed darker and more saturated. The pastel set above is
     built to GLOW — it is drawn additively onto near-black. Reused on a pale
     ground it disappears completely, so light mode gets its own palette that
     works subtractively instead. */
  var FILM_LIGHT = ['#0f9d8f', '#3f5fd0', '#8f4fc0', '#d0407f', '#b8790f'];

  function film(mode) {
    return mode === 'light' ? FILM_LIGHT : FILM_DARK;
  }

  var SPRITE_N = 7; /* distinct films cached */
  var SPRITE_PX = 168; /* sprite resolution; bubbles are drawn at or below this */

  /* A bubble on a DARK ground is drawn additively: it is pure added light, so
     the body can stay almost transparent and the rim glows. On a LIGHT ground
     none of that works — you cannot add light to near-white. A real bubble seen
     against a bright sky reads by SUBTRACTING: a thin dark contour, iridescent
     bands, a bright specular. So the two modes are genuinely different
     renderers, not one renderer with a recoloured palette. */
  function makeSpriteLight(filmOffset) {
    var c = document.createElement('canvas');
    c.width = c.height = SPRITE_PX;
    var g = c.getContext('2d');
    if (!g) return c;

    var mid = SPRITE_PX / 2;
    var r = mid - SPRITE_PX * 0.055;
    var F = FILM_LIGHT;

    /* 1. Soft contact shadow, so the sphere separates from the pale ground. */
    var halo = g.createRadialGradient(mid, mid, r * 0.82, mid, mid, r * 1.07);
    halo.addColorStop(0, 'rgba(24, 52, 84, 0.1)');
    halo.addColorStop(1, 'rgba(24, 52, 84, 0)');
    g.fillStyle = halo;
    g.beginPath();
    g.arc(mid, mid, r * 1.07, 0, Math.PI * 2);
    g.fill();

    /* 2. Body — transparent in the middle, darkening toward the edge where the
       film is seen at a glancing angle, then a bright ring just inside the rim. */
    var body = g.createRadialGradient(
      mid - r * 0.24,
      mid - r * 0.28,
      r * 0.04,
      mid,
      mid,
      r
    );
    body.addColorStop(0, 'rgba(255, 255, 255, 0.16)');
    body.addColorStop(0.45, 'rgba(40, 80, 120, 0.03)');
    body.addColorStop(0.78, 'rgba(28, 68, 108, 0.1)');
    body.addColorStop(0.93, 'rgba(22, 58, 96, 0.17)');
    body.addColorStop(0.985, 'rgba(255, 255, 255, 0.34)');
    body.addColorStop(1, 'rgba(255, 255, 255, 0)');
    g.fillStyle = body;
    g.beginPath();
    g.arc(mid, mid, r, 0, Math.PI * 2);
    g.fill();

    /* 3. Iridescent rim — stronger than in dark mode, since it is now carrying
       the colour instead of a glow. */
    var rimW = r * 0.11;
    var rimR = r - rimW * 0.5;
    g.save();
    g.lineWidth = rimW;
    g.globalAlpha = 0.72;
    if (typeof g.createConicGradient === 'function') {
      var conic = g.createConicGradient((filmOffset * Math.PI * 2) / SPRITE_N, mid, mid);
      for (var i = 0; i <= F.length; i++) conic.addColorStop(i / F.length, F[i % F.length]);
      g.strokeStyle = conic;
      g.beginPath();
      g.arc(mid, mid, rimR, 0, Math.PI * 2);
      g.stroke();
    } else {
      var seg = (Math.PI * 2) / F.length;
      var base = (filmOffset * Math.PI * 2) / SPRITE_N;
      g.shadowBlur = rimW * 2.2;
      for (var sx = 0; sx < F.length; sx++) {
        g.strokeStyle = F[sx];
        g.shadowColor = F[sx];
        g.beginPath();
        g.arc(mid, mid, rimR, base + sx * seg - 0.06, base + (sx + 1) * seg + 0.06);
        g.stroke();
      }
    }
    g.restore();

    /* 4. The defining contour. On a light ground this single dark hairline is
       what makes the shape read as a sphere at small sizes. */
    g.save();
    g.globalAlpha = 0.34;
    g.lineWidth = r * 0.028;
    g.strokeStyle = 'rgba(18, 46, 76, 1)';
    g.beginPath();
    g.arc(mid, mid, r - rimW * 0.15, 0, Math.PI * 2);
    g.stroke();
    g.restore();

    /* 5. Specular highlight. */
    g.save();
    g.translate(mid - r * 0.33, mid - r * 0.4);
    g.rotate(-0.38);
    var spec = g.createRadialGradient(0, 0, 0, 0, 0, r * 0.24);
    spec.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
    spec.addColorStop(0.45, 'rgba(255, 255, 255, 0.5)');
    spec.addColorStop(1, 'rgba(255, 255, 255, 0)');
    g.fillStyle = spec;
    g.beginPath();
    if (typeof g.ellipse === 'function') g.ellipse(0, 0, r * 0.23, r * 0.15, 0, 0, Math.PI * 2);
    else g.arc(0, 0, r * 0.19, 0, Math.PI * 2);
    g.fill();
    g.restore();

    /* 6. Bounce light, lower-right. */
    g.save();
    g.globalAlpha = 0.5;
    g.lineWidth = r * 0.05;
    g.strokeStyle = 'rgba(255, 255, 255, 1)';
    g.beginPath();
    g.arc(mid, mid, r * 0.78, Math.PI * 0.16, Math.PI * 0.56);
    g.stroke();
    g.restore();

    return c;
  }

  function makeSprite(filmOffset) {
    var c = document.createElement('canvas');
    c.width = c.height = SPRITE_PX;
    var g = c.getContext('2d');
    if (!g) return c;

    var mid = SPRITE_PX / 2;
    /* Leave a margin so the outer glow and rim stroke are not clipped. */
    var r = mid - SPRITE_PX * 0.055;

    /* ── 1. Outer glow ──────────────────────────────────────────────────── */
    var glow = g.createRadialGradient(mid, mid, r * 0.86, mid, mid, r * 1.06);
    glow.addColorStop(0, 'rgba(180, 235, 245, 0.16)');
    glow.addColorStop(1, 'rgba(180, 235, 245, 0)');
    g.fillStyle = glow;
    g.beginPath();
    g.arc(mid, mid, r * 1.06, 0, Math.PI * 2);
    g.fill();

    /* ── 2. Body wash ───────────────────────────────────────────────────────
       A soap bubble is nearly invisible in its middle and only gathers
       brightness toward the edge, so the gradient is centred OFF the middle
       (up-left, toward the notional light) and stays almost transparent until
       it approaches the rim. */
    var body = g.createRadialGradient(
      mid - r * 0.26,
      mid - r * 0.3,
      r * 0.04,
      mid,
      mid,
      r
    );
    body.addColorStop(0, 'rgba(255, 255, 255, 0.055)');
    body.addColorStop(0.42, 'rgba(255, 255, 255, 0.012)');
    body.addColorStop(0.76, 'rgba(210, 240, 250, 0.028)');
    body.addColorStop(0.93, 'rgba(255, 255, 255, 0.115)');
    body.addColorStop(1, 'rgba(255, 255, 255, 0.02)');
    g.fillStyle = body;
    g.beginPath();
    g.arc(mid, mid, r, 0, Math.PI * 2);
    g.fill();

    /* ── 3. Iridescent rim ──────────────────────────────────────────────────
       createConicGradient is the correct tool and gives a continuous film. It
       is missing in the older Chromium builds this page has to survive, so the
       fallback paints the same five stops as discrete arcs with a soft shadow
       to blur their seams — visually close, and it never leaves the rim blank. */
    var rimW = r * 0.1;
    var rimR = r - rimW * 0.5;
    g.save();
    g.lineWidth = rimW;
    /* Raised from 0.5. With the body wash dialled down (it was reading as a grey
       disc rather than as film), the rim is what has to sell the sphere. */
    g.globalAlpha = 0.64;

    if (typeof g.createConicGradient === 'function') {
      var conic = g.createConicGradient((filmOffset * Math.PI * 2) / SPRITE_N, mid, mid);
      for (var i = 0; i <= FILM_DARK.length; i++) {
        conic.addColorStop(i / FILM_DARK.length, FILM_DARK[i % FILM_DARK.length]);
      }
      g.strokeStyle = conic;
      g.beginPath();
      g.arc(mid, mid, rimR, 0, Math.PI * 2);
      g.stroke();
    } else {
      var seg = (Math.PI * 2) / FILM_DARK.length;
      var base = (filmOffset * Math.PI * 2) / SPRITE_N;
      g.shadowBlur = rimW * 2.2;
      for (var s = 0; s < FILM_DARK.length; s++) {
        g.strokeStyle = FILM_DARK[s];
        g.shadowColor = FILM_DARK[s];
        g.beginPath();
        /* Overlap each arc slightly so the seams are not visible as gaps. */
        g.arc(mid, mid, rimR, base + s * seg - 0.06, base + (s + 1) * seg + 0.06);
        g.stroke();
      }
    }
    g.restore();

    /* ── 4. Inner edge darkening ────────────────────────────────────────────
       A thin darker line just inside the rim is what makes the sphere read as
       a closed surface rather than a flat ring. */
    g.save();
    g.globalAlpha = 0.1;
    g.lineWidth = r * 0.03;
    g.strokeStyle = 'rgba(16, 30, 44, 1)';
    g.beginPath();
    g.arc(mid, mid, r - rimW * 1.15, 0, Math.PI * 2);
    g.stroke();
    g.restore();

    /* ── 5. Specular highlight ──────────────────────────────────────────────
       The single most important cue. An ellipse, rotated, up and left of centre
       — the reflection of the light source in a curved film. */
    g.save();
    g.translate(mid - r * 0.33, mid - r * 0.4);
    g.rotate(-0.38);
    var spec = g.createRadialGradient(0, 0, 0, 0, 0, r * 0.24);
    spec.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
    spec.addColorStop(0.45, 'rgba(255, 255, 255, 0.36)');
    spec.addColorStop(1, 'rgba(255, 255, 255, 0)');
    g.fillStyle = spec;
    g.beginPath();
    /* ellipse() is well supported, but guard it — without the guard a missing
       ellipse() would throw inside the render loop and kill the whole field. */
    if (typeof g.ellipse === 'function') {
      g.ellipse(0, 0, r * 0.23, r * 0.15, 0, 0, Math.PI * 2);
    } else {
      g.arc(0, 0, r * 0.19, 0, Math.PI * 2);
    }
    g.fill();
    g.restore();

    /* ── 6. Secondary bounce light ──────────────────────────────────────────
       Dim, lower-right, opposite the specular — light bounced back up off the
       ground. Cheap, and it stops the bubble looking lit from one side only. */
    g.save();
    g.globalAlpha = 0.2;
    g.lineWidth = r * 0.055;
    g.strokeStyle = 'rgba(255, 255, 255, 1)';
    g.beginPath();
    g.arc(mid, mid, r * 0.78, Math.PI * 0.16, Math.PI * 0.56);
    g.stroke();
    g.restore();

    return c;
  }

  /* One cache per mode, built lazily. Switching theme therefore costs a single
     sprite build the first time and nothing on every switch after that. */
  var SPRITES = { dark: null, light: null };
  function sprites(mode) {
    var key = mode === 'light' ? 'light' : 'dark';
    if (!SPRITES[key]) {
      var out = [];
      for (var i = 0; i < SPRITE_N; i++) {
        out.push(key === 'light' ? makeSpriteLight(i) : makeSprite(i));
      }
      SPRITES[key] = out;
    }
    return SPRITES[key];
  }

  /* ── The field ──────────────────────────────────────────────────────────── */
  function BubbleField(canvas, opts) {
    this.canvas = canvas;
    this.g = canvas.getContext('2d');
    if (!this.g) return;

    opts = opts || {};
    this.density = opts.density || 1; /* bubbles per 26k css px², scaled */
    this.rMin = opts.rMin || 9;
    this.rMax = opts.rMax || 46;
    this.riseMin = opts.riseMin || 12; /* css px per second */
    this.riseMax = opts.riseMax || 46;
    this.popOnTap = opts.popOnTap !== false;

    this.mode = opts.mode === 'light' ? 'light' : 'dark';
    this.bubbles = [];
    this.pops = [];
    this.w = 0;
    this.h = 0;
    this.dpr = 1;
    this.target = 0;
    this.running = false;
    this.visible = true;
    this.inView = true;
    this.last = 0;
    this.t = 0;
    this.wind = 0;
    this.windTarget = 0;
    this.gust = 0; /* scroll-driven upward kick, decays */

    /* Frame-time ring buffer for retune(). 40 samples ≈ 0.7s at 60fps. */
    this.samples = [];
    this.tuneAt = 0;

    this.resize();
    this.seed();
    this.bind();
  }

  BubbleField.prototype.resize = function () {
    var c = this.canvas;
    var rect = c.getBoundingClientRect();
    var w = Math.max(1, Math.round(rect.width));
    var h = Math.max(1, Math.round(rect.height));
    /* DPR is capped at 2. A 3x Android panel triples fill cost for a
       difference nobody can see on a translucent bubble. */
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    if (w === this.w && h === this.h && dpr === this.dpr) return;
    this.w = w;
    this.h = h;
    this.dpr = dpr;
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
    this.g.setTransform(dpr, 0, 0, dpr, 0, 0);

    /* Ceiling scales with area so a desktop hero is not as sparse as a phone's,
       then is clamped so a very tall viewport cannot ask for 400 bubbles.
       The divisor was 26000 and measured far too sparse: a 320px hero is ~1160
       css px tall, so 320x1160 asked for only 14 bubbles across two screens of
       height — an empty sky. 13000 puts ~28 in that hero and ~105 on a 1440
       desktop, which is the density the sprite cache was built to afford. */
    var byArea = Math.round(((w * h) / 13000) * this.density);
    this.maxCount = Math.max(22, Math.min(130, byArea));
    if (!this.target) this.target = this.maxCount;
    this.target = Math.min(this.target, this.maxCount);
  };

  BubbleField.prototype.make = function (atBottom) {
    var r = this.rMin + Math.pow(Math.random(), 1.7) * (this.rMax - this.rMin);
    /* z is depth: 0 = far, 1 = near. Size, speed, alpha and blur all read off
       it, which is what turns a flat scatter into a field with air in it. */
    var z = (r - this.rMin) / (this.rMax - this.rMin);
    return {
      x: Math.random() * this.w,
      y: atBottom ? this.h + r + Math.random() * this.h * 0.4 : Math.random() * this.h,
      r: r,
      z: z,
      rise: this.riseMin + z * (this.riseMax - this.riseMin),
      /* Horizontal sway — amplitude larger for lighter bubbles. */
      swayA: (5 + Math.random() * 22) * (1.25 - z * 0.55),
      swayF: 0.16 + Math.random() * 0.42,
      swayP: Math.random() * Math.PI * 2,
      /* Wobble: the film flexes as it rises. Volume-preserving squash. */
      wobA: 0.02 + Math.random() * 0.055,
      wobF: 0.5 + Math.random() * 1.5,
      wobP: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.34,
      rot: Math.random() * Math.PI * 2,
      sprite: (Math.random() * SPRITE_N) | 0,
      alpha: 0.4 + z * 0.5,
      /* Fade-in so a recycled bubble never pops into existence mid-screen. */
      born: 0
    };
  };

  BubbleField.prototype.seed = function () {
    this.bubbles.length = 0;
    for (var i = 0; i < this.target; i++) this.bubbles.push(this.make(false));
  };

  /* Burst a bubble into an expanding ring plus droplets. */
  BubbleField.prototype.pop = function (b) {
    var drops = [];
    var n = 5 + ((Math.random() * 5) | 0);
    for (var i = 0; i < n; i++) {
      var a = (i / n) * Math.PI * 2 + Math.random() * 0.6;
      var sp = b.r * (1.4 + Math.random() * 2.2);
      drops.push({
        x: b.x,
        y: b.y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - b.r * 0.5,
        r: Math.max(0.9, b.r * (0.06 + Math.random() * 0.11))
      });
    }
    this.pops.push({
      x: b.x,
      y: b.y,
      r: b.r,
      life: 0,
      dur: 0.42 + Math.random() * 0.14,
      film: film(this.mode)[(Math.random() * FILM_DARK.length) | 0],
      drops: drops
    });
  };

  /* Pop the nearest bubble to a point, if one is close enough to feel intended.
     Returns true when something popped, so the caller can tell a hit from a
     miss (a miss must not be treated as an interaction). */
  BubbleField.prototype.popAt = function (px, py) {
    var best = -1;
    var bestD = 1e9;
    for (var i = 0; i < this.bubbles.length; i++) {
      var b = this.bubbles[i];
      var dx = b.x - px;
      var dy = b.y - py;
      var d = Math.sqrt(dx * dx + dy * dy);
      /* Generous but bounded hit radius — a finger is imprecise, yet tapping
         empty air should still feel like empty air. */
      if (d < b.r + 18 && d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best < 0) return false;
    this.pop(this.bubbles[best]);
    this.bubbles[best] = this.make(true);
    return true;
  };

  /* A deliberate burst from a screen point — used when an item is added to the
     bag, so the confirmation is felt as well as read. */
  BubbleField.prototype.burstAt = function (px, py, n) {
    n = n || 9;
    for (var i = 0; i < n; i++) {
      var b = this.make(false);
      b.x = px + (Math.random() - 0.5) * 40;
      b.y = py + (Math.random() - 0.5) * 22;
      b.rise = this.riseMax * (1.5 + Math.random());
      b.born = 0;
      if (this.bubbles.length < this.maxCount + 24) this.bubbles.push(b);
      else this.bubbles[(Math.random() * this.bubbles.length) | 0] = b;
    }
    this.gust = Math.max(this.gust, 0.7);
  };

  /* Adapt COUNT (never quality) to measured frame time. */
  BubbleField.prototype.retune = function () {
    var s = this.samples;
    if (s.length < 40) return;
    var sum = 0;
    for (var i = 0; i < s.length; i++) sum += s[i];
    var avg = sum / s.length;
    s.length = 0;

    if (avg > 21 && this.target > 14) {
      /* Missing 60fps — shed 12% and try again next window. */
      this.target = Math.max(14, Math.round(this.target * 0.88));
      this.trim();
    } else if (avg < 13.2 && this.target < this.maxCount) {
      this.target = Math.min(this.maxCount, this.target + 4);
    }
  };

  BubbleField.prototype.trim = function () {
    while (this.bubbles.length > this.target) this.bubbles.pop();
  };

  BubbleField.prototype.step = function (dt) {
    this.t += dt;

    /* Wind eases toward its target so pointer movement feels like air being
       pushed, not like a value being set. */
    this.wind += (this.windTarget - this.wind) * Math.min(1, dt * 3.2);
    this.windTarget *= 0.94;
    this.gust *= Math.pow(0.06, dt);

    var i;
    for (i = 0; i < this.bubbles.length; i++) {
      var b = this.bubbles[i];
      if (b.born < 1) b.born = Math.min(1, b.born + dt * 1.7);

      b.y -= b.rise * dt * (1 + this.gust * 1.9);
      b.x += (this.wind * (1.3 - b.z * 0.6) + Math.sin(this.t * b.swayF + b.swayP) * 0.5) * dt * 32;
      b.rot += b.spin * dt;

      /* Recycle above the top, and wrap horizontally so a strong wind cannot
         empty one side of the field. */
      if (b.y + b.r < -12) {
        this.bubbles[i] = this.make(true);
        continue;
      }
      if (b.x < -b.r - 30) b.x = this.w + b.r + 20;
      else if (b.x > this.w + b.r + 30) b.x = -b.r - 20;
    }

    /* Top up toward target after a retune raised it. */
    while (this.bubbles.length < this.target) this.bubbles.push(this.make(true));

    for (i = this.pops.length - 1; i >= 0; i--) {
      var p = this.pops[i];
      p.life += dt;
      for (var d = 0; d < p.drops.length; d++) {
        var o = p.drops[d];
        o.x += o.vx * dt;
        o.y += o.vy * dt;
        o.vy += 210 * dt; /* droplets fall */
        o.vx *= 0.985;
      }
      if (p.life >= p.dur) this.pops.splice(i, 1);
    }
  };

  BubbleField.prototype.draw = function () {
    var g = this.g;
    var light = this.mode === 'light';
    var sp = sprites(this.mode);
    g.clearRect(0, 0, this.w, this.h);

    /* Dark ground: 'lighter' makes overlapping films add up the way real ones
       do, and it lets the rim glow. Light ground: additive blending would erase
       the bubbles entirely (adding light to near-white is still white), so the
       light sprites are composited normally and read subtractively. */
    g.globalCompositeOperation = light ? 'source-over' : 'lighter';
    /* Light sprites carry their own contour, so they need less alpha headroom
       than a glow does. */
    var aScale = light ? 0.92 : 1;

    var i;
    for (i = 0; i < this.bubbles.length; i++) {
      var b = this.bubbles[i];
      var wob = Math.sin(this.t * b.wobF + b.wobP) * b.wobA;
      /* Squash one axis and stretch the other by the same factor, so the film
         changes shape without appearing to change volume. */
      var sx = (1 + wob) * b.r;
      var sy = (1 - wob) * b.r;

      g.globalAlpha = b.alpha * b.born * aScale;
      g.save();
      g.translate(b.x, b.y);
      g.rotate(b.rot);
      /* One drawImage per bubble. This is the whole performance story. */
      g.drawImage(sp[b.sprite], -sx, -sy, sx * 2, sy * 2);
      g.restore();
    }

    for (i = 0; i < this.pops.length; i++) {
      var p = this.pops[i];
      var k = p.life / p.dur; /* 0 → 1 */
      var fade = 1 - k;

      /* Expanding ring, thinning as it goes. */
      g.globalAlpha = fade * 0.75;
      g.strokeStyle = p.film;
      g.lineWidth = Math.max(0.6, p.r * 0.15 * fade);
      g.beginPath();
      g.arc(p.x, p.y, p.r * (1 + k * 0.85), 0, Math.PI * 2);
      g.stroke();

      /* Droplets. */
      g.globalAlpha = fade * 0.9;
      g.fillStyle = p.film;
      for (var d = 0; d < p.drops.length; d++) {
        var o = p.drops[d];
        g.beginPath();
        g.arc(o.x, o.y, o.r * (1 - k * 0.35), 0, Math.PI * 2);
        g.fill();
      }
    }

    g.globalAlpha = 1;
    g.globalCompositeOperation = 'source-over';
  };

  BubbleField.prototype.frame = function (now) {
    if (!this.running) return;
    var dt = this.last ? (now - this.last) / 1000 : 0.016;
    this.last = now;
    /* Clamp dt so returning to a backgrounded tab does not teleport the field. */
    if (dt > 0.05) dt = 0.05;

    this.step(dt);
    this.draw();

    this.samples.push(now - (this.frameStart || now));
    this.frameStart = now;
    if (this.samples.length >= 40) this.retune();

    var self = this;
    this.raf = window.requestAnimationFrame(function (t) {
      self.frame(t);
    });
  };

  /* Swap renderer when the theme changes. Repaints immediately so a paused or
     reduced-motion field does not sit there in the previous theme's colours. */
  BubbleField.prototype.setMode = function (mode) {
    var next = mode === 'light' ? 'light' : 'dark';
    if (next === this.mode) return;
    this.mode = next;
    if (this.g) this.draw();
  };

  BubbleField.prototype.play = function () {
    if (this.running || !this.g) return;
    if (!this.visible || !this.inView) return;
    this.running = true;
    this.last = 0;
    this.frameStart = 0;
    var self = this;
    this.raf = window.requestAnimationFrame(function (t) {
      self.frame(t);
    });
  };

  BubbleField.prototype.stop = function () {
    this.running = false;
    if (this.raf) window.cancelAnimationFrame(this.raf);
    this.raf = 0;
  };

  /* A single composed frame for prefers-reduced-motion. The field is still
     THERE — the page would lose its subject without it — it simply does not
     move. */
  BubbleField.prototype.still = function () {
    if (!this.g) return;
    this.t = 3.4;
    this.draw();
  };

  BubbleField.prototype.bind = function () {
    var self = this;

    var ro = null;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(function () {
        self.resize();
        if (reduceMotion) self.still();
      });
      ro.observe(this.canvas);
    } else {
      window.addEventListener('resize', function () {
        self.resize();
        if (reduceMotion) self.still();
      });
    }

    /* Pause when off screen or the tab is hidden. A bubble field nobody is
       looking at is pure battery drain, and on this page's traffic battery
       anxiety is a real bounce cause. */
    if (window.IntersectionObserver) {
      new IntersectionObserver(
        function (entries) {
          self.inView = entries[0].isIntersecting;
          if (self.inView && !reduceMotion) self.play();
          else self.stop();
        },
        { rootMargin: '80px' }
      ).observe(this.canvas);
    }

    document.addEventListener('visibilitychange', function () {
      self.visible = !document.hidden;
      if (self.visible && !reduceMotion) self.play();
      else self.stop();
    });
  };

  /* Attach pointer interaction to a HOST element rather than to the canvas.
     The canvas keeps `pointer-events: none` so it can never swallow a tap
     meant for a CTA sitting above it; the host sees the same coordinates and
     only pops when the tap did not land on something interactive. */
  BubbleField.prototype.attachPointer = function (host) {
    if (!host || !this.popOnTap) return;
    var self = this;

    host.addEventListener(
      'pointermove',
      function (ev) {
        var rect = self.canvas.getBoundingClientRect();
        if (self.lastPx != null) {
          self.windTarget += ((ev.clientX - self.lastPx) / rect.width) * 5.5;
          if (self.windTarget > 4) self.windTarget = 4;
          if (self.windTarget < -4) self.windTarget = -4;
        }
        self.lastPx = ev.clientX;
      },
      { passive: true }
    );

    host.addEventListener(
      'pointerdown',
      function (ev) {
        /* Never steal a tap aimed at a control. */
        if (ev.target.closest && ev.target.closest('a, button, input, label, details, summary')) {
          return;
        }
        var rect = self.canvas.getBoundingClientRect();
        self.popAt(ev.clientX - rect.left, ev.clientY - rect.top);
      },
      { passive: true }
    );
  };

  /* ══════════════════════════════════════════════════════════════════════════
     2. COPY — every visible string, so the two languages cannot drift.
     English is read out of the server-rendered markup (so the page still reads
     correctly with JS off, and there is only one hand-written half to keep).
     ══════════════════════════════════════════════════════════════════════════ */
  var BN = {
    'nav.why': 'কেন',
    'nav.styles': 'ডিজাইন',
    'nav.how': 'কীভাবে চলে',
    'nav.faq': 'প্রশ্ন',
    'nav.order': 'অর্ডার করুন',

    'tape.cod': 'সারা বাংলাদেশে ক্যাশ অন ডেলিভারি',
    'tape.count': '৯টি বাবল ব্লাস্টার ডিজাইন',
    'tape.price': 'শুরু ৳৩৫০ থেকে',
    'tape.off': 'সব ডিজাইনে ২৮% পর্যন্ত ছাড়',
    'tape.safe': 'প্রতিটা বক্সেই বাবল সলিউশন দেওয়া আছে',
    'tape.ship': 'ঢাকায় ১–৩ দিনে ডেলিভারি',

    'hero.badge': 'সীমিত স্টক · আজই অর্ডার করুন',
    'hero.t1': 'দশ মিনিটেই',
    'hero.t2': 'সারাটা বিকেল ওদের।',
    'hero.lead':
      'ফোন হাতে নিয়ে বসে থাকার বদলে বাচ্চারা ছুটে বেড়াবে হাজারটা বাবলের পেছনে। ট্রিগার চাপলেই সেকেন্ডে শত শত বাবল — বাগানে, ছাদে, ঘরের ভেতরেও। প্রতিটা বক্সেই এক বোতল বাবল সলিউশন দেওয়া আছে।',
    'hero.cta1': 'ডিজাইন দেখুন',
    'hero.cta2': 'কীভাবে চলে',
    'hero.save': 'সেভ করুন ৳৩১০ · ৯টি ডিজাইন, শুরু ৳৩৫০ থেকে',
    'hero.tagLbl': 'আজকের দাম',
    'trust.cod': 'ক্যাশ অন ডেলিভারি',
    'trust.safe': 'সলিউশন বক্সেই আছে',
    'trust.age': '৩ বছর ও তার বেশি',
    'trust.ship': '১–৩ দিনে ঢাকায়',

    'why.kicker': 'বাবা-মায়ের জন্য',
    'why.title': 'বাচ্চার হাতে ফোন নয়, বাবল দিন।',
    'why.lead':
      'আমরা সবাই জানি স্ক্রিনটা কত সহজে বাচ্চাকে শান্ত করে দেয় — আর সেটা নিয়ে খারাপও লাগে। একটা বাবল ব্লাস্টার সেই জায়গাটা নেয়: হাত নাড়ে, দৌড়ায়, হাসে, ক্লান্ত হয়ে ঘুমায়।',
    'why.c1t': 'স্ক্রিন ছাড়াই ব্যস্ত',
    'why.c1d':
      'ট্যাব বন্ধ করতে বলার ঝগড়া নেই। ট্রিগার চাপলেই বাবল, আর বাচ্চা নিজে থেকেই বাইরে চলে যায়।',
    'why.c2t': 'ভাই-বোন একসাথে খেলে',
    'why.c2d':
      'একজন চালায়, বাকিরা বাবল ফাটায়। ঝগড়া না করে একসাথে খেলার সবচেয়ে সহজ খেলনা।',
    'why.c3t': 'যে গিফটটা আসলে খেলা হয়',
    'why.c3d':
      'জন্মদিন হোক বা এমনি — এই গিফট আলমারিতে পড়ে থাকে না। প্রথম দিনেই খোলা হয়, আর রোজ চলে।',
    'why.c4t': 'ছবি তোলার মতো সুন্দর',
    'why.c4d':
      'বিকেলের আলোয় হাজারটা বাবল। বাচ্চার সেই হাসির ছবিটা আপনার ফোনেই থেকে যাবে।',

    'styles.kicker': '৯টি ডিজাইন',
    'styles.title': 'ওর পছন্দের ক্যারেক্টারটা বেছে নিন।',
    'styles.lead':
      'ডাইনোসর, জিরাফ, গ্যাটলিং — প্রতিটাই একই রকম বাবল দেয়, শুধু চেহারাটা আলাদা। রঙ বেছে ব্যাগে যোগ করুন।',
    'styles.pickColour': 'রঙ বেছে নিন',
    'styles.add': 'ব্যাগে যোগ করুন',
    'styles.added': 'যোগ হয়েছে ✓',
    'styles.left': 'টি বাকি',
    'styles.stock': 'স্টকে আছে',
    'styles.oos': 'স্টক শেষ',

    'bag.kicker': 'আপনার ব্যাগ',
    'bag.title': 'অর্ডারটা শেষ করুন।',
    'bag.lead': 'কোনো অ্যাডভান্স লাগবে না। ডেলিভারিম্যানকে হাতে হাতে টাকা দিন।',
    'bag.panelT': 'ব্যাগে যা আছে',
    'bag.emptyT': 'ব্যাগ এখন খালি',
    'bag.emptyD': 'উপরের ডিজাইনগুলো থেকে পছন্দের ব্লাস্টার বেছে নিন।',
    'bag.emptyCta': 'ডিজাইন দেখুন',
    'bag.clear': 'সব মুছুন',

    'refill.t': 'পার্টির মাঝপথে বাবল শেষ হলে?',
    'refill.d':
      'একটা বড় রিফিল বোতল সাথে নিয়ে নিন — বাবল শেষ হয়ে কান্না শুরু হওয়ার আগেই। প্রতিটা ব্লাস্টারেই চলে।',
    'refill.add': 'রিফিল যোগ করুন',
    'refill.added': 'যোগ হয়েছে ✓',

    'sum.t': 'অর্ডার সারসংক্ষেপ',
    'sum.inside': 'ঢাকার ভিতরে',
    'sum.outside': 'ঢাকার বাইরে',
    'sum.items': 'পণ্যের দাম',
    'sum.saved': 'আপনি বাঁচালেন',
    'sum.ship': 'ডেলিভারি চার্জ',
    'sum.free': 'ফ্রি',
    'sum.total': 'সর্বমোট',
    'sum.cta': 'ক্যাশ অন ডেলিভারিতে অর্ডার করুন',
    'sum.ctaEmpty': 'আগে একটা ব্লাস্টার বেছে নিন',
    'sum.adding': 'অপেক্ষা করুন…',
    'sum.note': 'অর্ডার কনফার্ম করতে আমরা ফোন করব। পণ্য হাতে পেয়ে টাকা দিন।',
    'sum.err': 'কিছু একটা ভুল হয়েছে। আবার চেষ্টা করুন।',
    'sum.errStock': 'দুঃখিত, এই পরিমাণ স্টকে নেই। সংখ্যা কমিয়ে দেখুন।',
    'sum.freeNote': 'ফ্রি ডেলিভারি পেতে আর',

    'det.t': 'ডেলিভারির তথ্য',
    'det.d': 'শুধু চারটা ঘর — বাকিটা আমরা ফোনে কনফার্ম করে নেব।',
    'det.name': 'আপনার নাম',
    'det.phone': 'মোবাইল নম্বর',
    'det.address': 'পুরো ঠিকানা',
    'det.city': 'এলাকা / শহর',
    'det.namePh': 'যেমন — রুবেল আহমেদ',
    'det.phonePh': '01XXXXXXXXX',
    'det.addressPh': 'বাসা, রোড, ব্লক',
    'det.cityPh': 'যেমন — মিরপুর, ঢাকা',
    'det.errName': 'নামটা লিখুন',
    'det.errPhone': 'সঠিক ১১ সংখ্যার নম্বর দিন',
    'det.errAddress': 'পুরো ঠিকানাটা লিখুন',
    'det.errCity': 'এলাকার নাম লিখুন',

    'how.kicker': 'কীভাবে চলে',
    'how.title': 'বক্স খুলে খেলা শুরু — দুই মিনিট।',
    'how.s1t': 'সলিউশন ভরুন',
    'how.s1d': 'বক্সে দেওয়া বাবল সলিউশন ট্যাংকে ঢালুন। শেষ হলে ৳৬০-এর রিফিল দিয়ে আবার ভরুন।',
    'how.s2t': 'ব্যাটারি লাগিয়ে নিন',
    'how.s2d':
      'এগুলো ব্যাটারিতে চলে, আর প্যাকেজে ব্যাটারির কথা লেখা নেই — তাই ঘরে এক সেট রাখুন, প্রথম দিনেই খেলা শুরু হয়ে যাবে।',
    'how.s3t': 'ট্রিগার চাপুন',
    'how.s3d': 'চাপ দিলেই সেকেন্ডে শত শত বাবল, সাথে লাইট আর আওয়াজ। বাকিটা বাচ্চার কাজ।',

    'safe.kicker': 'বাবা-মায়ের প্রশ্ন',
    'safe.title': 'যা যা জেনে রাখা দরকার।',
    'safe.f1t': 'শিশুদের জন্য নিরাপদ ফর্মুলা',
    'safe.f1d':
      'পানি-ভিত্তিক সাবান সলিউশন — নির্মাতার ভাষায় শিশুদের জন্য নিরাপদ ফর্মুলা। মেশানোর দরকার নেই, ঢেলেই ব্যবহার করুন।',
    'safe.f2t': '৩ বছর ও তার বেশি',
    'safe.f2d': 'ছোট যন্ত্রাংশ আছে, তাই ৩ বছরের নিচে বড়দের সাথে খেলতে দিন।',
    'safe.f3t': 'হালকা, ছোট হাতের জন্য',
    'safe.f3d':
      'টেকসই ABS প্লাস্টিক, বাচ্চার হাতের মাপে গ্রিপ — পুরো খেলা জুড়ে ধরে রাখার মতো হালকা।',
    'safe.f4t': '৭ দিনে বদল',
    'safe.f4d': 'পণ্য নষ্ট বা কাজ না করলে ৭ দিনের মধ্যে বদলে দেওয়া হবে।',

    'faq.kicker': 'প্রশ্ন ও উত্তর',
    'faq.title': 'আরও কিছু জানতে চান?',
    'faq.q1': 'বাবল সলিউশন কি সাথে দেওয়া আছে?',
    'faq.a1':
      'হ্যাঁ। প্রতিটা ব্লাস্টারের সাথে এক বোতল বাবল সলিউশন দেওয়া থাকে, তাই বক্স খুলেই খেলা শুরু করা যায়। এগুলো ব্যাটারিতে চলে, তবে প্যাকেজে ব্যাটারির কথা লেখা নেই — তাই এক সেট হাতে রাখুন। রিফিল ৳৬০ থেকে শুরু।',
    'faq.q2': 'কত বছরের বাচ্চার জন্য?',
    'faq.a2':
      '৩ বছর ও তার বেশি বয়সের জন্য। ছোট যন্ত্রাংশ থাকায় ৩ বছরের নিচের বাচ্চাদের বড়দের সাথে খেলতে দেওয়াই ভালো।',
    'faq.q3': 'ডেলিভারি চার্জ কত আর কত দিন লাগে?',
    'faq.a3':
      'ঢাকার ভিতরে ৳৭০, ঢাকার বাইরে ৳১৩০। ঢাকায় সাধারণত ১–৩ দিন, ঢাকার বাইরে ২–৫ দিন লাগে। সারা বাংলাদেশে ক্যাশ অন ডেলিভারি।',
    'faq.q4': 'অ্যাডভান্স টাকা দিতে হবে?',
    'faq.a4':
      'না। পণ্য হাতে পেয়ে ডেলিভারিম্যানকে টাকা দিন। অর্ডারের পর আমরা ফোন করে কনফার্ম করে নেব।',
    'faq.q5': 'পণ্য নষ্ট আসলে বা কাজ না করলে?',
    'faq.a5':
      'ডেলিভারির ৭ দিনের মধ্যে জানালে বদলে দেওয়া হবে। ফোন বা হোয়াটসঅ্যাপে ০১৩১৫৭০৬৬১৬ নম্বরে যোগাযোগ করুন।',
    'faq.q6': 'একটার বেশি নিলে কি সুবিধা আছে?',
    'faq.a6':
      'ভাই-বোন থাকলে দুটো নেওয়াই ভালো — একটা নিয়ে দুজনের ঝগড়া হয়। একই অর্ডারে যতগুলোই নিন, ডেলিভারি চার্জ একবারই — ঢাকার ভিতরে ৳৭০।',

    'close.t': 'আজ অর্ডার করলে এই সপ্তাহেই ওর হাতে।',
    'close.d':
      'স্টক সীমিত — কিছু রঙ ইতিমধ্যেই শেষের দিকে। ক্যাশ অন ডেলিভারি, কোনো অ্যাডভান্স নেই।',
    'close.cta': 'ডিজাইন বেছে নিন',

    'dock.k': 'সর্বমোট',
    'dock.cta': 'অর্ডার করুন',

    'toast.added': 'ব্যাগে যোগ হয়েছে',
    'toast.removed': 'ব্যাগ থেকে বাদ দেওয়া হয়েছে',
    'toast.cleared': 'ব্যাগ খালি করা হয়েছে',
    'toast.max': 'এর বেশি স্টকে নেই'
  };

  var EN = {};
  var lang = 'en';

  function T(key) {
    if (lang === 'bn' && BN[key] != null) return BN[key];
    return EN[key] != null ? EN[key] : '';
  }

  function readEnglish() {
    var nodes = root.querySelectorAll('[data-i18n], [data-i18n-html]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var key = n.getAttribute('data-i18n') || n.getAttribute('data-i18n-html');
      if (key && EN[key] == null) {
        EN[key] = n.getAttribute('data-i18n-html') ? n.innerHTML : n.textContent;
      }
    }
    /* Placeholders live on the same elements but in a different attribute. */
    var phs = root.querySelectorAll('[data-i18n-ph]');
    for (var j = 0; j < phs.length; j++) {
      var k = phs[j].getAttribute('data-i18n-ph');
      if (k && EN[k] == null) EN[k] = phs[j].getAttribute('placeholder') || '';
    }
  }

  function applyLang() {
    document.documentElement.setAttribute('data-lang', lang);
    var nodes = root.querySelectorAll('[data-i18n], [data-i18n-html]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var htmlKey = n.getAttribute('data-i18n-html');
      var key = htmlKey || n.getAttribute('data-i18n');
      if (!key) continue;
      var val = T(key);
      if (!val) continue;
      if (htmlKey) n.innerHTML = val;
      else n.textContent = val;
    }
    var phs = root.querySelectorAll('[data-i18n-ph]');
    for (var j = 0; j < phs.length; j++) {
      var pv = T(phs[j].getAttribute('data-i18n-ph'));
      if (pv) phs[j].setAttribute('placeholder', pv);
    }
    var segs = root.querySelectorAll('[data-lang-btn]');
    for (var s = 0; s < segs.length; s++) {
      segs[s].setAttribute(
        'aria-pressed',
        segs[s].getAttribute('data-lang-btn') === lang ? 'true' : 'false'
      );
    }
    render();
  }

  function setLang(next) {
    lang = next === 'bn' ? 'bn' : 'en';
    try {
      localStorage.setItem('tlb-lang', lang);
    } catch (e) {}
    applyLang();
  }

  /* ── Money ──────────────────────────────────────────────────────────────── */
  var BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];

  function digits(str) {
    if (lang !== 'bn') return String(str);
    return String(str).replace(/[0-9]/g, function (d) {
      return BN_DIGITS[+d];
    });
  }

  function money(n) {
    var s = Math.round(n)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '৳' + digits(s);
  }

  /* ══════════════════════════════════════════════════════════════════════════
     3. BAG STATE
     ══════════════════════════════════════════════════════════════════════════ */
  /* bag: array of { vid, pi, vi, qty } — an array (not a map) so the display
     order is the order things were added, which is what a shopper expects. */
  var bag = [];
  var area = 'inside';
  var busy = false;

  function findVariant(vid) {
    for (var p = 0; p < STYLES.length; p++) {
      var vs = STYLES[p].vs || [];
      for (var v = 0; v < vs.length; v++) {
        if (String(vs[v].id) === String(vid)) return { pi: p, vi: v, p: STYLES[p], v: vs[v] };
      }
    }
    if (REFILL) {
      var rv = REFILL.vs || [];
      for (var r = 0; r < rv.length; r++) {
        if (String(rv[r].id) === String(vid)) return { pi: -1, vi: r, p: REFILL, v: rv[r] };
      }
    }
    return null;
  }

  function bagIndex(vid) {
    for (var i = 0; i < bag.length; i++) if (String(bag[i].vid) === String(vid)) return i;
    return -1;
  }

  function bagCount() {
    var n = 0;
    for (var i = 0; i < bag.length; i++) n += bag[i].qty;
    return n;
  }

  function itemsTotal() {
    var t = 0;
    for (var i = 0; i < bag.length; i++) {
      var f = findVariant(bag[i].vid);
      if (f) t += f.v.p * bag[i].qty;
    }
    return t;
  }

  function comparedTotal() {
    var t = 0;
    for (var i = 0; i < bag.length; i++) {
      var f = findVariant(bag[i].vid);
      if (f) t += (f.v.cmp || f.v.p) * bag[i].qty;
    }
    return t;
  }

  function shipCost() {
    if (!bag.length) return 0;
    if (FREE_OVER && itemsTotal() >= FREE_OVER) return 0;
    return area === 'outside' ? DELIVERY.outside : DELIVERY.inside;
  }

  function saveBag() {
    try {
      var slim = [];
      for (var i = 0; i < bag.length; i++) slim.push([bag[i].vid, bag[i].qty]);
      localStorage.setItem('tlb-bag', JSON.stringify({ b: slim, a: area }));
    } catch (e) {}
  }

  function loadBag() {
    try {
      var raw = localStorage.getItem('tlb-bag');
      if (!raw) return;
      var o = JSON.parse(raw);
      if (o && o.a) area = o.a === 'outside' ? 'outside' : 'inside';
      if (!o || !o.b) return;
      for (var i = 0; i < o.b.length; i++) {
        var vid = o.b[i][0];
        var qty = o.b[i][1];
        var f = findVariant(vid);
        /* Re-validate against the live catalogue: a variant that has sold out
           or been removed since the visitor's last session must not silently
           reappear in their bag and then fail at checkout. */
        if (!f || !f.v.a) continue;
        bag.push({ vid: vid, qty: Math.max(1, Math.min(qty, f.v.q || 1)) });
      }
    } catch (e) {}
  }

  function addToBag(vid, silent, fromEl) {
    var f = findVariant(vid);
    if (!f || !f.v.a) return false;
    var i = bagIndex(vid);
    var have = i < 0 ? 0 : bag[i].qty;
    var cap = f.v.q != null ? f.v.q : 99;
    if (have >= cap) {
      toast(T('toast.max'));
      return false;
    }
    if (i < 0) bag.push({ vid: vid, qty: 1 });
    else bag[i].qty++;
    saveBag();
    render();
    if (!silent) {
      toast(T('toast.added'));
      celebrate(fromEl);
    }
    return true;
  }

  function setQty(vid, qty) {
    var i = bagIndex(vid);
    if (i < 0) return;
    var f = findVariant(vid);
    var cap = f && f.v.q != null ? f.v.q : 99;
    if (qty > cap) {
      toast(T('toast.max'));
      qty = cap;
    }
    if (qty <= 0) {
      bag.splice(i, 1);
      toast(T('toast.removed'));
    } else {
      bag[i].qty = qty;
    }
    saveBag();
    render();
  }

  function clearBag() {
    bag.length = 0;
    saveBag();
    render();
    toast(T('toast.cleared'));
  }

  /* ══════════════════════════════════════════════════════════════════════════
     4. ELEMENT REFS
     ══════════════════════════════════════════════════════════════════════════ */
  function q(sel) {
    return root.querySelector(sel);
  }
  function qa(sel) {
    return root.querySelectorAll(sel);
  }

  var el = {
    nav: q('[data-nav]'),
    spineNow: q('[data-spine-now]'),
    navCart: q('[data-nav-cart]'),
    navCartN: q('[data-nav-cart-n]'),
    lines: q('[data-lines]'),
    empty: q('[data-empty]'),
    clear: q('[data-clear]'),
    refillBtn: q('[data-refill-add]'),
    rowItems: q('[data-row-items]'),
    rowSave: q('[data-row-save]'),
    rowSaveWrap: q('[data-row-save-wrap]'),
    rowShip: q('[data-row-ship]'),
    total: q('[data-total]'),
    cta: q('[data-cta]'),
    ctaLabel: q('[data-cta-label]'),
    msg: q('[data-msg]'),
    freeNote: q('[data-free-note]'),
    details: q('[data-details]'),
    dock: q('[data-dock]'),
    dockV: q('[data-dock-v]'),
    toast: q('[data-toast]'),
    pick: q('[data-pick]'),
    heroImgs: qa('[data-hero-img]'),
    heroDots: qa('[data-hero-dot]'),
    heroTagP: q('[data-hero-tag-p]'),
    heroNow: q('[data-hero-now]'),
    heroWas: q('[data-hero-was]')
  };

  /* ══════════════════════════════════════════════════════════════════════════
     5. RENDER
     ══════════════════════════════════════════════════════════════════════════ */
  function toast(text) {
    if (!el.toast || !text) return;
    el.toast.textContent = text;
    el.toast.setAttribute('data-show', 'true');
    if (toast._t) window.clearTimeout(toast._t);
    toast._t = window.setTimeout(function () {
      el.toast.setAttribute('data-show', 'false');
    }, 2200);
  }

  function setMsg(text, kind) {
    if (!el.msg) return;
    el.msg.textContent = text || '';
    if (kind) el.msg.setAttribute('data-kind', kind);
    else el.msg.removeAttribute('data-kind');
  }

  /* Confirm the add physically, in the page's own vocabulary.
     Two layers, because one is not enough on a long page: a soap-coloured ring
     out of the button itself (always), plus a real bubble burst on the canvas
     when the pressed control happens to sit over the hero field. */
  function celebrate(fromEl) {
    if (!fromEl) return;

    if (!reduceMotion) {
      fromEl.setAttribute('data-pop', 'true');
      window.setTimeout(function () {
        fromEl.removeAttribute('data-pop');
      }, 560);
    }

    if (reduceMotion || !heroField) return;
    var hero = q('.tlb-hero');
    if (!hero) return;
    var hr = heroField.canvas.getBoundingClientRect();
    var br = fromEl.getBoundingClientRect();
    var cx = br.left + br.width / 2 - hr.left;
    var cy = br.top + br.height / 2 - hr.top;
    /* Only when the pressed control is actually over the hero canvas; a burst
       behind a section the visitor cannot see is wasted work. */
    if (cy < -40 || cy > hr.height + 40) return;
    heroField.burstAt(cx, cy, 10);
  }

  function lineHtml(item, f) {
    var v = f.v;
    var p = f.p;
    var img = v.img || p.img || '';
    var sub = v.n ? v.n : '';
    return (
      '<div class="tlb-line" data-line="' + v.id + '">' +
      (img
        ? '<img class="tlb-line__img" src="' + img + '" alt="" loading="lazy" width="56" height="56">'
        : '<span class="tlb-line__img"></span>') +
      '<div class="tlb-line__mid">' +
      '<div class="tlb-line__n">' + p.t + '</div>' +
      (sub ? '<div class="tlb-line__v">' + sub + ' · ' + money(v.p) + '</div>' : '') +
      '</div>' +
      '<div class="tlb-line__right">' +
      '<span class="tlb-line__p">' + money(v.p * item.qty) + '</span>' +
      '<span class="tlb-line__ctl">' +
      '<button type="button" class="tlb-line__b" data-dec="' + v.id + '" aria-label="Decrease">&minus;</button>' +
      '<span class="tlb-line__q">' + digits(item.qty) + '</span>' +
      '<button type="button" class="tlb-line__b" data-inc="' + v.id + '" aria-label="Increase">+</button>' +
      '</span>' +
      '</div>' +
      '</div>'
    );
  }

  function renderLines() {
    if (!el.lines) return;
    var html = '';
    for (var i = 0; i < bag.length; i++) {
      var f = findVariant(bag[i].vid);
      if (f) html += lineHtml(bag[i], f);
    }
    el.lines.innerHTML = html;
    if (el.empty) el.empty.hidden = bag.length > 0;
    if (el.clear) el.clear.hidden = bag.length === 0;
  }

  function renderSummary() {
    var items = itemsTotal();
    var cmp = comparedTotal();
    var saved = Math.max(0, cmp - items);
    var ship = shipCost();
    var total = items + ship;

    if (el.rowItems) el.rowItems.textContent = money(items);
    if (el.rowSaveWrap) el.rowSaveWrap.hidden = saved <= 0;
    if (el.rowSave) el.rowSave.textContent = '− ' + money(saved);
    if (el.rowShip) {
      el.rowShip.textContent = !bag.length
        ? '—'
        : ship === 0
        ? T('sum.free')
        : money(ship);
      el.rowShip.parentNode.setAttribute('data-free', ship === 0 && bag.length ? 'true' : 'false');
    }
    if (el.total) el.total.textContent = money(total);
    if (el.dockV) el.dockV.textContent = money(total);

    /* Free-delivery nudge — a concrete, honest "add ৳X more" line. */
    if (el.freeNote) {
      var gap = FREE_OVER - items;
      if (FREE_OVER && bag.length && gap > 0) {
        el.freeNote.hidden = false;
        el.freeNote.textContent = T('sum.freeNote') + ' ' + money(gap) + '.';
      } else {
        el.freeNote.hidden = true;
      }
    }

    if (el.cta) el.cta.disabled = busy;
    if (el.ctaLabel) {
      el.ctaLabel.textContent = busy
        ? T('sum.adding')
        : bag.length
        ? T('sum.cta')
        : T('sum.ctaEmpty');
    }

    if (el.navCart) {
      var n = bagCount();
      el.navCart.setAttribute('data-show', n > 0 ? 'true' : 'false');
      if (el.navCartN) el.navCartN.textContent = digits(n);
    }

    if (el.dock) {
      var show = bag.length > 0;
      el.dock.setAttribute('data-show', show ? 'true' : 'false');
      document.body.setAttribute('data-has-dock', show ? 'true' : 'false');
    }

    if (el.details) el.details.hidden = bag.length === 0 || el.details.dataset.opened !== 'true';
  }

  /* Reflect bag state back onto the gallery cards, so the grid and the bag can
     never disagree about what has been chosen. */
  function renderCards() {
    var cards = qa('[data-prod]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var pi = +card.getAttribute('data-prod');
      var st = STYLES[pi];
      if (!st) continue;

      var sel = +(card.getAttribute('data-sel') || 0);
      var v = (st.vs || [])[sel];
      if (!v) continue;

      var idx = bagIndex(v.id);
      var qty = idx < 0 ? 0 : bag[idx].qty;
      card.setAttribute('data-in-bag', qty > 0 ? 'true' : 'false');

      var qn = card.querySelector('[data-qn]');
      if (qn) qn.textContent = digits(qty);

      var dec = card.querySelector('[data-cdec]');
      var inc = card.querySelector('[data-cinc]');
      if (inc) inc.disabled = v.q != null && qty >= v.q;
      if (dec) dec.disabled = qty <= 0;

      var now = card.querySelector('[data-now]');
      if (now) now.textContent = money(v.p);
      var was = card.querySelector('[data-was]');
      if (was) {
        if (v.cmp && v.cmp > v.p) {
          was.hidden = false;
          was.textContent = money(v.cmp);
        } else {
          was.hidden = true;
        }
      }
      var off = card.querySelector('[data-off]');
      if (off) {
        if (v.cmp && v.cmp > v.p) {
          off.hidden = false;
          off.textContent = '−' + digits(Math.round((1 - v.p / v.cmp) * 100)) + '%';
        } else {
          off.hidden = true;
        }
      }

      var stock = card.querySelector('[data-stock]');
      if (stock) {
        if (!v.a) {
          stock.textContent = T('styles.oos');
          stock.setAttribute('data-low', 'true');
        } else if (v.q != null && v.q <= 3) {
          stock.textContent = digits(v.q) + ' ' + T('styles.left');
          stock.setAttribute('data-low', 'true');
        } else {
          stock.textContent = T('styles.stock');
          stock.setAttribute('data-low', 'false');
        }
      }

      var addBtn = card.querySelector('[data-add]');
      if (addBtn) {
        addBtn.textContent = T('styles.add');
        addBtn.disabled = !v.a;
      }

      /* Swatch pressed state + the card image following the chosen colour. */
      var sws = card.querySelectorAll('[data-sw]');
      for (var s = 0; s < sws.length; s++) {
        sws[s].setAttribute('aria-pressed', +sws[s].getAttribute('data-sw') === sel ? 'true' : 'false');
      }
      var mainImg = card.querySelector('[data-cimg]');
      if (mainImg && v.img && mainImg.getAttribute('src') !== v.img) {
        mainImg.setAttribute('src', v.img);
      }
    }

    if (el.refillBtn && REFILL) {
      var rv = (REFILL.vs || [])[+(el.refillBtn.getAttribute('data-rv') || 0)];
      var inBag = rv && bagIndex(rv.id) >= 0;
      el.refillBtn.setAttribute('data-added', inBag ? 'true' : 'false');
      el.refillBtn.textContent = inBag ? T('refill.added') : T('refill.add');
    }
  }

  function render() {
    renderLines();
    renderSummary();
    renderCards();
    /* The hero prices are money() output too, so they have to be re-rendered
       on a language switch or they keep Latin digits while the rest of the page
       turns Bengali. render() is what applyLang calls, so this belongs here
       rather than in the language handler. */
    showHero(heroI);
  }

  /* ══════════════════════════════════════════════════════════════════════════
     6. VARIANT PICKER
     ══════════════════════════════════════════════════════════════════════════ */
  var pickPi = -1;
  var lastFocus = null;

  function openPicker(pi) {
    var st = STYLES[pi];
    if (!st || !el.pick) return;
    pickPi = pi;
    lastFocus = document.activeElement;

    var card = q('[data-prod="' + pi + '"]');
    var sel = card ? +(card.getAttribute('data-sel') || 0) : 0;

    var img = el.pick.querySelector('[data-pick-img]');
    var name = el.pick.querySelector('[data-pick-n]');
    var price = el.pick.querySelector('[data-pick-p]');
    var opts = el.pick.querySelector('[data-pick-opts]');
    if (img) img.setAttribute('src', (st.vs[sel] && st.vs[sel].img) || st.img || '');
    if (name) name.textContent = st.t;

    var v0 = st.vs[sel] || st.vs[0];
    if (price) {
      price.innerHTML =
        '<span class="tlb-prod__now">' + money(v0.p) + '</span>' +
        (v0.cmp && v0.cmp > v0.p
          ? '<span class="tlb-prod__was">' + money(v0.cmp) + '</span>' +
            '<span class="tlb-off">−' + digits(Math.round((1 - v0.p / v0.cmp) * 100)) + '%</span>'
          : '');
    }

    if (opts) {
      var html = '';
      for (var i = 0; i < st.vs.length; i++) {
        var v = st.vs[i];
        var low = v.a && v.q != null && v.q <= 3;
        var stockTxt = !v.a
          ? T('styles.oos')
          : low
          ? digits(v.q) + ' ' + T('styles.left')
          : T('styles.stock');
        html +=
          '<button type="button" class="tlb-opt" data-pick-opt="' + i + '"' +
          ' aria-pressed="' + (i === sel ? 'true' : 'false') + '"' +
          (v.a ? '' : ' disabled') + '>' +
          '<span class="tlb-opt__dot" style="background:' + swBg(v) + '"></span>' +
          '<span class="tlb-opt__n">' + v.n + '</span>' +
          '<span class="tlb-opt__s" data-low="' + (low || !v.a ? 'true' : 'false') + '">' +
          stockTxt + '</span>' +
          '</button>';
      }
      opts.innerHTML = html;
    }

    el.pick.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
    var first = el.pick.querySelector('.tlb-opt:not([disabled])');
    if (first) first.focus();
  }

  function closePicker() {
    if (!el.pick) return;
    el.pick.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
    pickPi = -1;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function swBg(v) {
    if (v.sw2) return 'linear-gradient(135deg,' + v.sw + ' 0 50%,' + v.sw2 + ' 50% 100%)';
    return v.sw || '#9aa3ad';
  }

  function selectVariant(pi, vi) {
    var card = q('[data-prod="' + pi + '"]');
    if (card) card.setAttribute('data-sel', String(vi));
    renderCards();
  }

  /* ══════════════════════════════════════════════════════════════════════════
     7. CHECKOUT — details → pre-filled Shopify cart permalink
     A theme cannot create an order, so the hand-off goes through
     /cart/{variant}:{qty},...?checkout[...]=...  A permalink also REPLACES the
     cart rather than adding to it, which rules out a stale cart contaminating
     the total the shopper just agreed to.
     ══════════════════════════════════════════════════════════════════════════ */
  function detField(name) {
    var e = q('[data-det="' + name + '"]');
    return e ? String(e.value || '').trim() : '';
  }

  function setFieldError(name, msg) {
    var e = q('[data-det="' + name + '"]');
    if (!e) return;
    e.setAttribute('data-invalid', msg ? 'true' : 'false');
    var holder = e.parentNode;
    var err = holder.querySelector('.tlb-field__err');
    if (msg) {
      if (!err) {
        err = document.createElement('span');
        err.className = 'tlb-field__err';
        holder.appendChild(err);
      }
      err.textContent = msg;
    } else if (err) {
      err.parentNode.removeChild(err);
    }
  }

  /* Bangladeshi mobile numbers: 11 digits starting 01, optionally +880/880. */
  function normalizeBdPhone(raw) {
    var d = String(raw || '').replace(/[^0-9]/g, '');
    if (d.indexOf('880') === 0) d = d.slice(3);
    if (d.length === 10 && d.indexOf('1') === 0) d = '0' + d;
    return /^01[3-9][0-9]{8}$/.test(d) ? d : '';
  }

  function validateDetails() {
    var ok = true;
    var name = detField('name');
    var phone = normalizeBdPhone(detField('phone'));
    var addr = detField('address');
    var city = detField('city');

    setFieldError('name', name.length >= 2 ? '' : T('det.errName'));
    if (name.length < 2) ok = false;
    setFieldError('phone', phone ? '' : T('det.errPhone'));
    if (!phone) ok = false;
    setFieldError('address', addr.length >= 6 ? '' : T('det.errAddress'));
    if (addr.length < 6) ok = false;
    setFieldError('city', city.length >= 2 ? '' : T('det.errCity'));
    if (city.length < 2) ok = false;

    if (!ok) {
      var bad = q('[data-det][data-invalid="true"]');
      if (bad) {
        bad.focus();
        bad.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
      return null;
    }
    return { name: name, phone: phone, addr: addr, city: city };
  }

  function permalink(d) {
    var parts = [];
    for (var i = 0; i < bag.length; i++) parts.push(bag[i].vid + ':' + bag[i].qty);
    var bits = String(d.name).split(' ');
    var qs = [];
    var add = function (k, v) {
      if (v) qs.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    };
    var sp = 'checkout[shipping_address]';
    add(sp + '[first_name]', bits[0] || d.name);
    add(sp + '[last_name]', bits.slice(1).join(' ') || '.');
    add(sp + '[address1]', d.addr);
    add(sp + '[city]', d.city);
    add(sp + '[country]', 'Bangladesh');
    add(sp + '[phone]', d.phone);
    /* Carried onto the order so packing slips and courier labels show where it
       came from and which delivery zone was quoted. */
    add('attributes[Source]', 'Bubble Blasters landing page');
    add('attributes[Delivery area]', area === 'outside' ? 'Outside Dhaka' : 'Inside Dhaka');
    return '/cart/' + parts.join(',') + (qs.length ? '?' + qs.join('&') : '');
  }

  function checkout() {
    if (busy) return;
    if (!bag.length) {
      jump('styles');
      return;
    }
    if (!el.details) return;

    /* First press opens the form. The shopper must never land on an empty
       Shopify checkout having already "finished" on this page. */
    if (el.details.dataset.opened !== 'true') {
      el.details.dataset.opened = 'true';
      el.details.hidden = false;
      var first = el.details.querySelector('[data-det]');
      el.details.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      if (first) {
        window.setTimeout(function () {
          first.focus();
        }, reduceMotion ? 0 : 400);
      }
      return;
    }

    var d = validateDetails();
    if (!d) return;

    busy = true;
    setMsg('');
    renderSummary();
    window.location.href = permalink(d);
  }

  /* ══════════════════════════════════════════════════════════════════════════
     8. SCROLL CHOREOGRAPHY
     ══════════════════════════════════════════════════════════════════════════ */
  function jump(id) {
    var target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function initReveal() {
    var items = qa('.tlb-rise');
    if (!window.IntersectionObserver) {
      for (var i = 0; i < items.length; i++) items[i].setAttribute('data-seen', 'true');
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            entries[i].target.setAttribute('data-seen', 'true');
            io.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );
    for (var j = 0; j < items.length; j++) io.observe(items[j]);
  }

  function initNavState() {
    var sections = [];
    var links = qa('[data-nav-link]');
    for (var i = 0; i < links.length; i++) {
      var id = links[i].getAttribute('data-nav-link');
      var sec = document.getElementById(id);
      if (sec) sections.push({ id: id, el: sec, link: links[i] });
    }

    var lastY = window.pageYOffset || 0;
    var ticking = false;

    function onScroll() {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;

      if (el.nav) el.nav.setAttribute('data-stuck', y > 8 ? 'true' : 'false');

      /* Scroll velocity feeds the hero field, so flicking the page fast sends a
         gust of bubbles up. Subtle, but it makes the field feel like air. */
      if (heroField && !reduceMotion) {
        var dv = Math.abs(y - lastY);
        if (dv > 26) heroField.gust = Math.min(1, heroField.gust + dv / 900);
      }
      lastY = y;

      var mid = y + window.innerHeight * 0.34;
      var on = null;
      for (var i = 0; i < sections.length; i++) {
        /* getBoundingClientRect + scroll, not offsetTop: every section's
           offsetParent is #tlb-root (it is positioned), so offsetTop is measured
           from the root rather than the document. That happens to be nearly
           right today only because the root starts at the top of the body — it
           would silently break the moment anything is added above it. */
        var top = sections[i].el.getBoundingClientRect().top + y;
        if (mid >= top) on = sections[i];
      }
      for (var j = 0; j < sections.length; j++) {
        sections[j].link.setAttribute('data-on', on === sections[j] ? 'true' : 'false');
      }
      if (el.spineNow && on) {
        var label = on.el.getAttribute('data-spine-label');
        if (label) el.spineNow.textContent = label;
      }
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(onScroll);
      },
      { passive: true }
    );
    onScroll();
  }

  /* ── Hero design cycling ────────────────────────────────────────────────
     The hero cycles DESIGNS, not colourways of one design, because "which
     character does my kid want" is the actual decision a parent is helping
     make — so the choice starts in the hero. The floating tag tracks the
     visible design's own price, or the shopper reads ৳880 under a ৳350 toy. */
  var HERO_SLIDES = DATA.heroSlides || [];
  var heroI = 0;
  var heroTimer = 0;

  function showHero(i) {
    if (!el.heroImgs.length) return;
    heroI = (i + el.heroImgs.length) % el.heroImgs.length;
    for (var j = 0; j < el.heroImgs.length; j++) {
      el.heroImgs[j].setAttribute('data-on', j === heroI ? 'true' : 'false');
    }
    for (var k = 0; k < el.heroDots.length; k++) {
      el.heroDots[k].setAttribute('data-on', k === heroI ? 'true' : 'false');
    }
    /* Every price in the hero tracks the visible design. Leaving the big
       figure on the flagship while the art cycled showed ৳880 above a ৳850
       toy — which reads as a mistake, or worse, as a bait price. */
    var slide = HERO_SLIDES[heroI];
    if (!slide) return;
    if (el.heroTagP) el.heroTagP.textContent = money(slide.p);
    if (el.heroNow) el.heroNow.textContent = money(slide.p);
    if (el.heroWas) {
      if (slide.cmp && slide.cmp > slide.p) {
        el.heroWas.hidden = false;
        el.heroWas.textContent = money(slide.cmp);
      } else {
        el.heroWas.hidden = true;
      }
    }
  }

  function heroAutoplay() {
    if (reduceMotion || el.heroImgs.length < 2) return;
    heroTimer = window.setInterval(function () {
      if (document.hidden) return;
      showHero(heroI + 1);
    }, 3800);
  }

  function stopHeroAutoplay() {
    if (heroTimer) window.clearInterval(heroTimer);
    heroTimer = 0;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     9. THEME
     ══════════════════════════════════════════════════════════════════════════ */
  function currentTheme() {
    var set = document.documentElement.getAttribute('data-theme');
    if (set) return set;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }

  /* Push the current theme into both canvases. The bubble renderer is not a
     recolour — dark composites additively and light composites normally — so a
     field left in the wrong mode is either invisible or a smear. */
  function syncFieldModes() {
    var m = currentTheme();
    if (heroField) heroField.setMode(m);
    if (closeField) closeField.setMode(m);
  }

  function toggleTheme() {
    var next = currentTheme() === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem('tlb-theme', next);
    } catch (e) {}
    syncFieldModes();
  }

  /* Follow the OS as well, for visitors who never touch the in-page toggle.
     currentTheme() returns the explicit data-theme when one is set, so this
     correctly does nothing once the visitor has made a choice. */
  function watchOsTheme() {
    if (!window.matchMedia) return;
    var mq = window.matchMedia('(prefers-color-scheme: dark)');
    var onChange = function () {
      syncFieldModes();
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  /* ══════════════════════════════════════════════════════════════════════════
     10. EVENTS — one delegated listener for the whole page
     ══════════════════════════════════════════════════════════════════════════ */
  function bindEvents() {
    root.addEventListener('click', function (ev) {
      var t = ev.target;
      var hit = function (attr) {
        var n = t.closest('[' + attr + ']');
        return n ? n : null;
      };

      var n;

      if ((n = hit('data-jump'))) {
        ev.preventDefault();
        jump(n.getAttribute('data-jump'));
        return;
      }

      if ((n = hit('data-lang-btn'))) {
        setLang(n.getAttribute('data-lang-btn'));
        return;
      }

      if (hit('data-theme-toggle')) {
        toggleTheme();
        return;
      }

      /* Gallery: colour swatch */
      if ((n = hit('data-sw'))) {
        var swCard = n.closest('[data-prod]');
        if (swCard) selectVariant(+swCard.getAttribute('data-prod'), +n.getAttribute('data-sw'));
        return;
      }

      /* Gallery: add */
      if ((n = hit('data-add'))) {
        var aCard = n.closest('[data-prod]');
        if (!aCard) return;
        var api = +aCard.getAttribute('data-prod');
        var asel = +(aCard.getAttribute('data-sel') || 0);
        var av = (STYLES[api].vs || [])[asel];
        if (av) addToBag(av.id, false, n);
        return;
      }

      /* Gallery: stepper */
      if ((n = hit('data-cinc')) || (n = hit('data-cdec'))) {
        var sCard = n.closest('[data-prod]');
        if (!sCard) return;
        var spi = +sCard.getAttribute('data-prod');
        var ssel = +(sCard.getAttribute('data-sel') || 0);
        var sv = (STYLES[spi].vs || [])[ssel];
        if (!sv) return;
        var si = bagIndex(sv.id);
        var scur = si < 0 ? 0 : bag[si].qty;
        setQty(sv.id, n.hasAttribute('data-cinc') ? scur + 1 : scur - 1);
        return;
      }

      /* Gallery: open colour picker */
      if ((n = hit('data-more'))) {
        var mCard = n.closest('[data-prod]');
        if (mCard) openPicker(+mCard.getAttribute('data-prod'));
        return;
      }

      /* Picker */
      if ((n = hit('data-pick-opt'))) {
        if (pickPi >= 0) {
          var pvi = +n.getAttribute('data-pick-opt');
          selectVariant(pickPi, pvi);
          var pv = (STYLES[pickPi].vs || [])[pvi];
          closePicker();
          if (pv) addToBag(pv.id, false, n);
        }
        return;
      }
      if (hit('data-pick-close')) {
        closePicker();
        return;
      }
      if (t === el.pick) {
        closePicker();
        return;
      }

      /* Bag lines */
      if ((n = hit('data-inc'))) {
        var iv = n.getAttribute('data-inc');
        var ii = bagIndex(iv);
        setQty(iv, (ii < 0 ? 0 : bag[ii].qty) + 1);
        return;
      }
      if ((n = hit('data-dec'))) {
        var dv = n.getAttribute('data-dec');
        var di = bagIndex(dv);
        setQty(dv, (di < 0 ? 0 : bag[di].qty) - 1);
        return;
      }
      if (hit('data-clear')) {
        clearBag();
        return;
      }

      /* Refill upsell */
      if ((n = hit('data-refill-add'))) {
        if (!REFILL) return;
        var rvi = +(n.getAttribute('data-rv') || 0);
        var rv = (REFILL.vs || [])[rvi];
        if (!rv) return;
        if (bagIndex(rv.id) >= 0) {
          setQty(rv.id, 0);
        } else {
          addToBag(rv.id, false, n);
        }
        return;
      }

      /* Delivery area */
      if ((n = hit('data-area'))) {
        area = n.getAttribute('data-area') === 'outside' ? 'outside' : 'inside';
        var abs = qa('[data-area]');
        for (var a = 0; a < abs.length; a++) {
          abs[a].setAttribute(
            'aria-pressed',
            abs[a].getAttribute('data-area') === area ? 'true' : 'false'
          );
        }
        saveBag();
        renderSummary();
        return;
      }

      /* Checkout */
      if (hit('data-cta') || hit('data-dock-cta')) {
        checkout();
        return;
      }

      /* Hero dots */
      if ((n = hit('data-hero-dot'))) {
        stopHeroAutoplay();
        showHero(+n.getAttribute('data-hero-dot'));
        return;
      }
    });

    /* Live-clear a field's error as soon as it becomes valid, so the form stops
       shouting the moment the shopper fixes it. */
    root.addEventListener('input', function (ev) {
      var f = ev.target.closest ? ev.target.closest('[data-det]') : null;
      if (!f) return;
      if (f.getAttribute('data-invalid') !== 'true') return;
      var nm = f.getAttribute('data-det');
      var val = String(f.value || '').trim();
      var good =
        nm === 'phone'
          ? !!normalizeBdPhone(val)
          : nm === 'address'
          ? val.length >= 6
          : val.length >= 2;
      if (good) setFieldError(nm, '');
    });

    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && el.pick && el.pick.getAttribute('data-open') === 'true') {
        closePicker();
      }
    });
  }

  /* ══════════════════════════════════════════════════════════════════════════
     11. BOOT
     ══════════════════════════════════════════════════════════════════════════ */
  var heroField = null;
  var closeField = null;

  function initFields() {
    var heroCanvas = q('[data-canvas="hero"]');
    if (heroCanvas) {
      heroField = new BubbleField(heroCanvas, {
        mode: currentTheme(),
        density: 1,
        rMin: 9,
        rMax: 48,
        riseMin: 14,
        riseMax: 52
      });
      if (reduceMotion) heroField.still();
      else {
        heroField.play();
        heroField.attachPointer(q('.tlb-hero'));
      }
    }

    /* The closing band gets the same engine at a calmer setting — bigger,
       slower, sparser bubbles, so it reads as the same world winding down
       rather than as a second attempt to impress. */
    var closeCanvas = q('[data-canvas="close"]');
    if (closeCanvas) {
      closeField = new BubbleField(closeCanvas, {
        mode: currentTheme(),
        density: 0.5,
        rMin: 14,
        rMax: 62,
        riseMin: 9,
        riseMax: 26
      });
      if (reduceMotion) closeField.still();
      else {
        closeField.play();
        closeField.attachPointer(q('.tlb-close'));
      }
    }
  }

  function boot() {
    readEnglish();

    try {
      var sl = localStorage.getItem('tlb-lang');
      if (sl === 'bn') lang = 'bn';
    } catch (e) {}

    loadBag();

    var abs = qa('[data-area]');
    for (var a = 0; a < abs.length; a++) {
      abs[a].setAttribute(
        'aria-pressed',
        abs[a].getAttribute('data-area') === area ? 'true' : 'false'
      );
    }

    bindEvents();
    initFields();
    watchOsTheme();
    initReveal();
    initNavState();
    showHero(0);
    heroAutoplay();
    applyLang();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
